import { Fragment, Suspense, useMemo, useRef, useState } from 'react';
import type { Placement } from 'react-aria';
import { metricWindowingFrontend, wneMetricConfigUnification } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toHref, toMetric } from '@gonfalon/navigator/navigators';
import { useSuspenseMetric } from '@gonfalon/rest-api/hooks/useSuspenseMetric';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { CopyToClipboard } from '@gonfalon/ui/CopyToClipboard';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import {
  Alert,
  Button,
  Code,
  Focusable,
  LinkButton,
  ProgressBar,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import type { IconName } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';
import { z } from 'zod';

import { CodeSpan } from '../../CodeSpan';
import {
  getMetricDenominatorValueColumn,
  getMetricValueColumn,
  supportsMetricValueColumn,
} from '../../getMetricValueColumn';
import { isImplicitWHNEventKey } from '../../isImplicitWHNEventKey';
import { MetricDefinition, metricDefinitionIncludesSuccessCriteria } from '../../MetricDefinition';
import { MetricEventDefault } from '../../MetricEventDefault';
import { HealthIndicator } from '../../MetricHealthCheck';
import type { MetricKind } from '../../MetricKind';
import { MetricLabel } from '../../MetricLabel';
import { MetricLastSeenInfo } from '../../MetricLastSeenInfo';
import { MetricMeasurementWindow } from '../../MetricMeasurementWindow';
import { MetricSuccessCriteria } from '../../MetricSuccessCriteria';
import { MetricVersionTag } from '../../MetricVersionTag';
import { hasWinsorization, MetricWinsorization } from '../../MetricWinsorization';
import { getRatioAggLabel } from '../../RatioMetricDefinitionBox';
import type { Metric } from '../../types';
import { useMetricDataSourceValueColumn } from '../../useMetricDataSourceValueColumn';

import styles from './MetricInfoPopover.module.css';

interface MetricInfoPopoverBaseProps {
  children: React.ReactNode;
  placement?: Placement;
  metricVersionId?: string;
  truncate?: boolean;
  /** Override the route project key when metric details live in a different project. */
  projectKey?: string;
  /**
   * Optional list of randomization units that are actually in use by the experiment or guarded rollout.
   * When provided, only these randomization units will be shown in the metric definition,
   * rather than all randomization units the metric supports.
   */
  randomizationUnitsInUse?: string[];
}

type MetricInfoPopoverProps = MetricInfoPopoverBaseProps &
  (
    | { metric: Metric; metricKey?: never }
    | { metric?: never; metricKey: string }
    | { metric: Metric; metricKey: string }
  );

function getRatioTermNoun(unitAggType: string | undefined, isNumeric: boolean | undefined): 'events' | 'values' | null {
  if (unitAggType === 'count_distinct') {
    return null;
  }
  if (isNumeric) {
    return 'values';
  }
  return 'events';
}

function RatioUnitsList({ units }: { units: string[] }) {
  if (units.length === 0) {
    return null;
  }
  return (
    <>
      {units.map((unit, index) => (
        <Fragment key={unit}>
          {index > 0 &&
            (index === units.length - 1
              ? msg('metrics.infoPopover.unitsLastJoiner')
              : msg('metrics.infoPopover.unitsJoiner'))}
          <Code size="small">{unit}</Code>
        </Fragment>
      ))}
    </>
  );
}

function RatioMetricDefinitionSentence({
  metric,
  randomizationUnitsInUse,
}: {
  metric: Metric;
  randomizationUnitsInUse?: string[];
}) {
  const denom = metric.denominator;
  if (!denom) {
    return null;
  }

  const metricUnits = metric.analysisUnits ?? [];
  const filtered = randomizationUnitsInUse
    ? metricUnits.filter((u) => randomizationUnitsInUse.includes(u))
    : metricUnits;
  const units = filtered.length > 0 ? filtered : metricUnits;

  const numeratorAgg = getRatioAggLabel(metric.unitAggregationType, metric.isNumeric);
  const denominatorAgg = getRatioAggLabel(denom.unitAggregationType, denom.isNumeric);

  const numeratorNoun = getRatioTermNoun(metric.unitAggregationType, metric.isNumeric);
  const denominatorNoun = getRatioTermNoun(denom.unitAggregationType, denom.isNumeric);

  // unitAggregationField exists on the API response for ratio metrics but is not
  // yet reflected in the generated MetricRep OpenAPI types — access it safely.
  const numeratorColumn = (metric as Metric & { unitAggregationField?: string }).unitAggregationField;
  const denominatorColumn = denom.unitAggregationField;

  const ratioTerm = (term: 'numerator' | 'denominator', noun: 'events' | 'values' | null, column?: string) =>
    noun ? (
      <Message id="metrics.infoPopover.ratioTermNoun" values={{ term, noun }} />
    ) : (
      <Message
        id="metrics.infoPopover.ratioTermColumn"
        values={{ term, code: () => (column ? <Code size="small">{column}</Code> : null) }}
      />
    );

  return (
    <span className={styles.ratioDefinitionSentence} id="metricDefinition">
      <Message
        id="metrics.infoPopover.ratioSentence"
        values={{
          hasUnits: units.length > 0 ? 'true' : 'false',
          numeratorAgg: () => <Code size="small">{numeratorAgg}</Code>,
          numeratorTerm: () => ratioTerm('numerator', numeratorNoun, numeratorColumn),
          denominatorAgg: () => <Code size="small">{denominatorAgg}</Code>,
          denominatorTerm: () => ratioTerm('denominator', denominatorNoun, denominatorColumn),
          units: () => <RatioUnitsList units={units} />,
        }}
      />
    </span>
  );
}

function LastSeenAndLink({ metric, projectKey }: { metric: Metric; projectKey: string }) {
  return (
    <div className={styles.lastSeenAndLink}>
      <MetricLastSeenInfo metric={metric} hideTooltip />
      <LinkButton
        target="_blank"
        variant="minimal"
        size="small"
        href={toHref(toMetric({ projectKey, metricKey: metric.key }))}
      >
        <Icon name="link-external" size="tiny" />
        {msg('metrics.infoPopover.viewDetails')}
      </LinkButton>
    </div>
  );
}

function MetricPopoverField({ icon, label, children }: { icon: IconName; label: string; children: React.ReactNode }) {
  return (
    <div className={styles.metricFieldRow}>
      <div className={styles.metricFieldLabel}>
        <Icon name={icon} size="tiny" aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className={styles.metricFieldValue}>{children}</div>
    </div>
  );
}

const metricPopoverDisplaySchema = z
  .object({
    isRatioMetric: z.boolean(),
    hasEventDefault: z.boolean(),
    hasSuccessCriteria: z.boolean(),
    hasUnit: z.boolean(),
    hasWindow: z.boolean(),
    hasRatioCustomWindow: z.boolean(),
    hasWinsor: z.boolean(),
    hasValueColumn: z.boolean(),
    hasRatioValueColumns: z.boolean(),
  })
  .transform((state) => ({
    ...state,
    showNonRatioMetricFields:
      !state.isRatioMetric &&
      (state.hasEventDefault ||
        state.hasSuccessCriteria ||
        state.hasUnit ||
        state.hasWindow ||
        state.hasWinsor ||
        state.hasValueColumn),
  }));

type MetricPopoverDisplayState = z.infer<typeof metricPopoverDisplaySchema>;

function deriveMetricPopoverDisplayState(
  metric: Metric,
  valueColumn: string | undefined,
  denominatorValueColumn: string | undefined,
): MetricPopoverDisplayState {
  const isRatioMetric = !!metric.denominator;
  // MetricMeasurementWindow returns null when windowStartOffset is non-zero but windowEndOffset is absent,
  // so exclude that case to avoid rendering an empty field label.
  const windowStartIsNonZeroAndNoEnd =
    metric.windowStartOffset !== undefined && metric.windowStartOffset !== 0 && metric.windowEndOffset === undefined;
  const hasSuccessCriteria =
    !isRatioMetric &&
    (!!metric.successCriteria || metric.kind === 'click' || metric.kind === 'pageview') &&
    !metricDefinitionIncludesSuccessCriteria(metric);
  const hasWindow =
    !isRatioMetric &&
    metricWindowingFrontend() &&
    (metric.windowStartOffset !== undefined || metric.windowEndOffset !== undefined) &&
    !windowStartIsNonZeroAndNoEnd;
  const hasRatioCustomWindow =
    isRatioMetric &&
    metricWindowingFrontend() &&
    (metric.windowStartOffset !== undefined ||
      metric.windowEndOffset !== undefined ||
      metric.denominator?.windowStartOffset !== undefined ||
      metric.denominator?.windowEndOffset !== undefined);
  const hasValueColumn = !isRatioMetric && wneMetricConfigUnification() && !!valueColumn;
  const hasRatioValueColumns =
    isRatioMetric && wneMetricConfigUnification() && !!(valueColumn || denominatorValueColumn);

  return metricPopoverDisplaySchema.parse({
    isRatioMetric,
    hasEventDefault: !!metric.eventDefault,
    hasSuccessCriteria,
    hasUnit: !!metric.unit,
    hasWindow,
    hasRatioCustomWindow,
    hasWinsor: hasWinsorization(metric),
    hasValueColumn,
    hasRatioValueColumns,
  });
}

const MetricInfoPopoverContent: React.FC<{
  metric: Metric;
  projectKey: string;
  randomizationUnitsInUse?: string[];
}> = ({ metric, projectKey, randomizationUnitsInUse }) => {
  const valueColumnEnabled = wneMetricConfigUnification();
  const dataSourceValueColumn = useMetricDataSourceValueColumn(projectKey, valueColumnEnabled);
  const valueColumn =
    valueColumnEnabled && supportsMetricValueColumn(metric)
      ? (getMetricValueColumn(metric) ?? dataSourceValueColumn(metric.dataSource?.key))
      : undefined;
  const denominatorValueColumn =
    valueColumnEnabled && supportsMetricValueColumn(metric.denominator)
      ? (getMetricDenominatorValueColumn(metric.denominator) ??
        dataSourceValueColumn(metric.denominator?.dataSource?.key ?? metric.dataSource?.key))
      : undefined;
  const displayState = useMemo(
    () => deriveMetricPopoverDisplayState(metric, valueColumn, denominatorValueColumn),
    [denominatorValueColumn, metric, valueColumn],
  );
  const {
    isRatioMetric,
    hasSuccessCriteria,
    hasWindow,
    hasRatioCustomWindow,
    hasWinsor,
    hasRatioValueColumns,
    showNonRatioMetricFields,
  } = displayState;
  const numeratorValueColumn = hasRatioValueColumns ? valueColumn : undefined;
  const ratioDenominatorValueColumn = hasRatioValueColumns ? denominatorValueColumn : undefined;

  // Grouped under the event key when there is one, otherwise shown under the definition
  // so trace metrics (filters, no event key) don't lose the indicator.
  const hasDisplayableEventKey =
    !!metric.eventKey && !isImplicitWHNEventKey({ eventKey: metric.eventKey, dataSourceKey: metric.dataSource?.key });
  const filtersIndicator = !!metric.filters && (
    <span className={styles.filtersApplied}>
      <Icon name="filter-list" size="tiny" aria-hidden="true" />{' '}
      <Message id="metrics.infoPopover.eventFiltersApplied" />
    </span>
  );

  const healthIndicator = !isRatioMetric ? (
    <HealthIndicator
      kind="icon"
      metric={{
        kind: metric.kind,
        eventKey: metric?.eventKey || '',
        dataSource: metric?.dataSource,
        _versionId: metric?._versionId,
        analysisUnits: metric?.analysisUnits || [],
        isNumeric: metric.isNumeric || false,
        successCriteria: metric?.successCriteria || undefined,
        unitAggregationType: metric?.unitAggregationType,
      }}
    />
  ) : null;
  const metricLabel = (
    <MetricLabel
      kind={(metric.kind as MetricKind) ?? 'click'}
      isNumeric={metric.isNumeric}
      unitAggregationType={metric.unitAggregationType}
    />
  );

  return (
    <div className={styles.infoContainer}>
      <div className={clsx(styles.titleRow, styles.titleRowWithMeta)}>
        <div className={styles.titleColumn}>
          <TooltipTrigger>
            <Focusable>
              <div
                className={styles.metricName}
                role="button"
                aria-label={msg('metrics.infoPopover.viewDetailsForLabel', { name: metric.name })}
              >
                {`${metric.name}`}
              </div>
            </Focusable>
            <Tooltip>{metric.name}</Tooltip>
          </TooltipTrigger>
        </div>
        {healthIndicator}
      </div>
      <div className={styles.metaRow}>
        {metricLabel}
        <MetricVersionTag withSeparator version={metric._version} />
      </div>
      {metric.description && <div className={styles.description}>{metric.description}</div>}

      <div className={styles.popoverBody}>
        <div className={styles.definitionSection}>
          <span className={styles.definitionLabel}>
            <Message id="metrics.infoPopover.metricDefinition" />
          </span>

          {isRatioMetric ? (
            <>
              <RatioMetricDefinitionSentence metric={metric} randomizationUnitsInUse={randomizationUnitsInUse} />
              {filtersIndicator}
            </>
          ) : (
            <>
              <MetricDefinition metric={metric} randomizationUnitsInUse={randomizationUnitsInUse} />
              {!hasDisplayableEventKey && filtersIndicator}
            </>
          )}

          {hasRatioCustomWindow && (
            <span className={styles.windowNote}>
              <Icon name="info-circle-outline" size="tiny" aria-hidden="true" />
              <Message id="metrics.infoPopover.customWindowNote" />
            </span>
          )}
        </div>

        {!isRatioMetric && hasDisplayableEventKey && (
          <div className={styles.eventKeySection}>
            <span className={styles.definitionLabel}>
              <Message id="metrics.infoPopover.eventKey" />
            </span>
            <CopyToClipboard text={metric.eventKey ?? ''} tooltip={msg('metrics.infoPopover.copyEventKey')}>
              <Button
                size="small"
                data-test-id="event-key"
                aria-label={msg('metrics.infoPopover.copyEventKeyLabel', { eventKey: metric.eventKey ?? '' })}
                className={styles.eventKey}
              >
                <TextTruncator>{metric.eventKey}</TextTruncator>
                <Icon name="copy-clipboard" size="small" />
              </Button>
            </CopyToClipboard>
            {filtersIndicator}
          </div>
        )}

        {showNonRatioMetricFields && (
          <div className={styles.metricFields}>
            {valueColumn && (
              <MetricPopoverField icon="data" label={msg('metrics.infoPopover.numericValueColumn')}>
                <CodeSpan textValue={valueColumn} />
              </MetricPopoverField>
            )}
            {!!metric.eventDefault && (
              <MetricPopoverField
                icon="click"
                label={msg('metrics.infoPopover.unitsWithout', { kind: metric.kind ?? 'custom' })}
              >
                <MetricEventDefault metric={metric} />
              </MetricPopoverField>
            )}
            {hasSuccessCriteria && (
              <MetricPopoverField icon="bullseye-arrow" label={msg('metrics.infoPopover.successCriteria')}>
                <MetricSuccessCriteria metric={metric} />
              </MetricPopoverField>
            )}
            {metric.unit && (
              <MetricPopoverField icon="tools" label={msg('metrics.infoPopover.unitOfMeasure')}>
                {metric.unit}
              </MetricPopoverField>
            )}
            {hasWindow && (
              <MetricPopoverField icon="clock" label={msg('metrics.infoPopover.measurementWindow')}>
                <MetricMeasurementWindow
                  windowStartOffset={metric.windowStartOffset}
                  windowEndOffset={metric.windowEndOffset}
                />
              </MetricPopoverField>
            )}
            {hasWinsor && (
              <MetricPopoverField icon="chart-histogram" label={msg('metrics.infoPopover.winsorization')}>
                <MetricWinsorization metric={metric} />
              </MetricPopoverField>
            )}
          </div>
        )}

        {isRatioMetric && (metric.successCriteria || metric.unit || hasRatioValueColumns) && (
          <div className={styles.metricFieldsBordered}>
            {numeratorValueColumn && (
              <MetricPopoverField icon="data" label={msg('metrics.infoPopover.numeratorValueColumn')}>
                <CodeSpan textValue={numeratorValueColumn} />
              </MetricPopoverField>
            )}
            {ratioDenominatorValueColumn && (
              <MetricPopoverField icon="data" label={msg('metrics.infoPopover.denominatorValueColumn')}>
                <CodeSpan textValue={ratioDenominatorValueColumn} />
              </MetricPopoverField>
            )}
            {metric.successCriteria && (
              <MetricPopoverField icon="bullseye-arrow" label={msg('metrics.infoPopover.successCriteria')}>
                <MetricSuccessCriteria metric={metric} />
              </MetricPopoverField>
            )}
            {metric.unit && (
              <MetricPopoverField icon="tools" label={msg('metrics.infoPopover.unitOfMeasure')}>
                {metric.unit}
              </MetricPopoverField>
            )}
          </div>
        )}

        <LastSeenAndLink metric={metric} projectKey={projectKey} />
      </div>
    </div>
  );
};

const MetricInfoPopoverSuspenseMetric: React.FC<{
  projectKey: string;
  metricKey: string;
  metricVersionId?: string;
  randomizationUnitsInUse?: string[];
}> = ({ metricKey, metricVersionId, projectKey, randomizationUnitsInUse }) => {
  const { data: metric } = useSuspenseMetric({
    projectKey,
    metricKey: metricKey ?? '',
    params: { versionId: metricVersionId },
  });
  return (
    <MetricInfoPopoverContent
      metric={metric}
      projectKey={projectKey}
      randomizationUnitsInUse={randomizationUnitsInUse}
    />
  );
};

export const MetricInfoPopover: React.FC<MetricInfoPopoverProps> = ({
  children,
  metric,
  placement = 'top',
  metricKey,
  metricVersionId,
  truncate,
  randomizationUnitsInUse,
  projectKey: projectKeyOverride,
}) => {
  const routeProjectKey = useProjectKey();
  const projectKey = projectKeyOverride ?? routeProjectKey;
  const ref = useRef(null);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState<true | undefined>();

  return (
    <TooltipTrigger>
      <span className={truncate ? styles.linkWrapper : undefined} ref={ref}>
        {children}
      </span>
      <Tooltip
        isOpen={isHoveringTooltip}
        placement={placement}
        triggerRef={ref}
        className={styles.tooltip}
        variant="popover"
      >
        {/* Plain div, not a button: the copy control inside is interactive, so this wrapper
            must not be a button (no nested buttons). It only needs to track hover. */}
        <div
          style={{ all: 'unset' }}
          onMouseEnter={() => setIsHoveringTooltip(true)}
          onMouseLeave={() => setIsHoveringTooltip(undefined)}
        >
          {metric ? (
            <MetricInfoPopoverContent
              metric={metric}
              projectKey={projectKey}
              randomizationUnitsInUse={randomizationUnitsInUse}
            />
          ) : (
            <Suspense
              fallback={<ProgressBar aria-label={msg('metrics.status.loading')} isIndeterminate size="large" />}
            >
              <ErrorBoundary
                owner="team-agent-platform"
                severity="medium"
                fallbackRender={() => (
                  <Alert status="error">
                    <Message id="metrics.infoPopover.loadError" />
                  </Alert>
                )}
              >
                <MetricInfoPopoverSuspenseMetric
                  metricKey={metricKey ?? ''}
                  metricVersionId={metricVersionId}
                  projectKey={projectKey}
                  randomizationUnitsInUse={randomizationUnitsInUse}
                />
              </ErrorBoundary>
            </Suspense>
          )}
        </div>
      </Tooltip>
    </TooltipTrigger>
  );
};
